import { useSession } from "next-auth/react";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import FlexBox from "@/components/Layout/FlexBox";
import Layout from "@/components/Layout/Layout";
import SiteTitle from "@/components/SiteTitle";

import Box from "@mui/joy/Box";
import Card from "@mui/joy/Card";
import Button from "@mui/joy/Button";
import CardContent from "@mui/joy/CardContent";
import Typography from "@mui/joy/Typography";
import Grid from "@mui/joy/Grid";
import Tooltip from "@mui/joy/Tooltip";
import Divider from "@mui/joy/Divider";
import Avatar from "@mui/joy/Avatar";
import Chip from "@mui/joy/Chip";

import ManagePostModal from "@/components/PostEditor/ManagePostModal";
import { useTextEditorDrawer } from "@/contexts/TextEditorDrawerContext";

/* Icons */
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import YouTubeIcon from "@mui/icons-material/YouTube";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import TopicOutlinedIcon from "@mui/icons-material/TopicOutlined";
import PhishingIcon from "@mui/icons-material/Phishing";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import CreateIcon from "@mui/icons-material/Create";
import BarChartIcon from "@mui/icons-material/BarChart";
import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PublicIcon from "@mui/icons-material/Public";
import DraftsIcon from "@mui/icons-material/Drafts";

const Dashboard = () => {
  const { openManagePost, toggleDrawer } = useTextEditorDrawer();
  const { data: session, status } = useSession();
  const [content, setContent] = useState("");
  const [selectedPost, setSelectedPost] = useState(null);
  const [metrics, setMetrics] = useState(null);


  // we fetch from api with useEffect /api/dashboard-metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      const response = await fetch("/api/dashboard-metrics");
      const data = await response.json();
      setMetrics(data);
    };

    fetchMetrics();
  }, []);

  if (!metrics) return <div>Loading...</div>;

  const metricData = [
    { title: "Total Posts", value: metrics.totalPosts, icon: <CreateIcon />, color: "#2196f3" },
    { title: "Last 30 Days", value: metrics.last30Days, icon: <EventIcon />, color: "#673ab7" },
    { title: "Scheduled", value: metrics.scheduled, icon: <ScheduleIcon />, color: "#ff9800" },
    { title: "Published", value: metrics.published, icon: <PublicIcon />, color: "#4caf50" },
    { title: "Drafts", value: metrics.drafts, icon: <DraftsIcon />, color: "#f44336" }
  ];

  if (status === "loading") {
    return <div>Loading...</div>;
  }

  const userName = session?.session?.user?.name || "User";

  const contentWriters = [
    {
      id: "topic",
      title: "Topic Content Writer",
      description: "Generate a post from a topic.",
      icon: <TopicOutlinedIcon sx={{ fontSize: "3rem", color: "#E91E63" }} />,
      color: "#E91E63",
      link: "/app/generator/topic",
    },
    {
      id: "website",
      title: "Website Content Writer",
      description: "Generate a post from any website or blog.",
      icon: <LanguageOutlinedIcon sx={{ fontSize: "3rem", color: "#4494d3" }} />,
      color: "#4494d3",
      link: "/app/generator/website",
    },
    {
      id: "youtube",
      title: "Youtube Content Writer",
      description: "Generate a post from a Youtube video.",
      icon: <YouTubeIcon sx={{ fontSize: "3rem", color: "red" }} />,
      color: "red",
      link: "/app/generator/youtube",
    },
    {
      id: "document",
      title: "Document Content Writer",
      description: "Generate a post from Word, Excel, PowerPoint, or PDFs.",
      icon: <UploadFileIcon sx={{ fontSize: "3rem", color: "#9c27b0" }} />,
      color: "#9c27b0",
      link: "/app/generator/documents",
    },
    {
      id: "text",
      title: "Text Content Writer",
      description: "Paste any text and generate a post instantly.",
      icon: <TextFieldsIcon sx={{ fontSize: "3rem", color: "#f44336" }} />,
      color: "#f44336",
      link: "/app/generator/text",
    },
    {
      id: "hook",
      title: "Hook Content Generator",
      description: "Generate engaging hooks for your posts.",
      icon: <PhishingIcon sx={{ fontSize: "3rem", color: "#8bc34a" }} />,
      color: "#8bc34a",
      link: "/app/generator/hooks",
    },
    {
      id: "carousel",
      title: "LinkedIn Carousel Generator",
      description: "Create stunning LinkedIn carousels.",
      icon: <ViewCarouselIcon sx={{ fontSize: "3rem", color: "#03a9f4" }} />,
      color: "#03a9f4",
      link: "/app/carousel",
    },
    {
      id: "viral",
      title: "Viral Content Inspiration",
      description: "Create posts inspired by viral trends.",
      icon: <WhatshotIcon sx={{ fontSize: "3rem", color: "#ff5722" }} />,
      color: "#ff5722",
      link: "/app/content-inspiration",
    },
  ];


  return (
    <Layout>
      <FlexBox>
        {/* Floating Write Button */}
        <Button
          color="primary"
          onClick={toggleDrawer(true)}
          className="create-post-btn"
          sx={{
            position: "fixed",
            bottom: "50px",
            right: "30px",
            zIndex: 9,
            borderRadius: "50%",
            padding: "15px",
            background: "#007BFF",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
            "&:hover": { backgroundColor: "#0056b3" }
          }}
        >
          <CreateIcon sx={{ fontSize: "2.2rem", color: "#fff" }} />
        </Button>

        {/* Manage Post Modal */}
        {openManagePost && (
          <ManagePostModal
            open={openManagePost}
            toggleDrawer={toggleDrawer}
            content={content}
            setContent={setContent}
            session={session}
            postId={selectedPost}
          />
        )}

        {/* Welcome Header */}
        <Box sx={{ m: 5 }}>
          <Typography level="h2" sx={{ fontWeight: "bold", mb: 1 }}>
            👋 Welcome back, {userName}!
          </Typography>
          <Typography level="body-sm" sx={{ mb: 2 }}>
            Ready to generate high-quality LinkedIn posts? Choose a tool below.
          </Typography>


          <Grid container spacing={2} sx={{ mt: 3 }} className="dashboard-metrics">
            {metricData.map((metric, index) => (
              <Grid key={index} xs={6} sm={4} md={2.4}>
                <Card
                  variant="soft"
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    boxShadow: "sm",
                    background: "linear-gradient(135deg, #f7f8fc 0%, #eef1f6 100%)",
                    borderRadius: "12px",
                    minHeight: "100px",
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": { transform: "scale(1.05)", boxShadow: "0px 8px 16px rgba(0,0,0,0.1)" }
                  }}
                >
                  {/* Icon & Title in a row */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "44px",
                      height: "44px",
                      borderRadius: "50%",
                      backgroundColor: metric.color,
                      color: "white",
                      boxShadow: "0px 2px 6px rgba(0,0,0,0.1)", // Softer shadow
                      mr: 1
                    }}>
                      {metric.icon}
                    </Box>
                    <Typography level="title-sm" sx={{ fontWeight: "bold", color: "#333" }}>
                      {metric.title}
                    </Typography>
                  </Box>

                  {/* Metric Value */}
                  <Typography
                    component="div"
                    level="body-lg"
                    sx={{
                      fontWeight: "bold",
                      color: metric.value > 0 ? "#333" : "#B0B0B0",
                      minHeight: "24px"
                    }}
                  >
                    {metric.value > 0 ? metric.value : <Box sx={{ fontSize: "0.9rem", opacity: 0.6 }}>— No Data</Box>}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>




          {/* If the user hasn't created any posts, encourage them */}
          {metrics.totalPosts === 0 && (
            <Box sx={{
              textAlign: "center",
              mt: 4,
              mb: 4,
              p: 3,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #f7f8fc 0%, #eef1f6 100%)",
              boxShadow: "0px 4px 10px rgba(0,0,0,0.1)"
            }}>
              <Typography level="body-md" sx={{ mb: 2, color: "#555" }}>
                🚀 Ready to create your first post? Get started with AI-powered content now!
              </Typography>
              <Button
                variant="solid"
                sx={{
                  backgroundColor: "#007BFF",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  "&:hover": { backgroundColor: "#0056b3" }
                }}
              >
                ✨ Start Your First Post
              </Button>
            </Box>

          )}

          <Grid container spacing={2} sx={{ mt: 4 }} className="content-writer-card">
            {contentWriters.map((writer, index) => (
              <Grid key={index} xs={12} sm={6} md={4} lg={3}>
                <Tooltip title={writer.description} placement="top">
                  <Link href={writer.link} style={{ textDecoration: "none" }}>
                    <Card
                      sx={{
                        width: "100%",
                        minHeight: "12rem",
                        borderTop: `4px solid ${writer.color}`,
                        transition: "transform 0.2s ease-in-out, box-shadow 0.2s",
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        borderRadius: "10px",
                        background: "#fff",
                        boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
                        "&:hover": {
                          transform: "scale(1.05)",
                          boxShadow: "0px 8px 16px rgba(0,0,0,0.15)"
                        },
                      }}
                    >
                      <Box sx={{
                        width: "60px",
                        height: "60px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        backgroundColor: `${writer.color}22` // Lightened version of color
                      }}>
                        {writer.icon}
                      </Box>
                      <Typography level="title-md" sx={{ mt: 1, fontWeight: "bold" }}>
                        {writer.title}
                      </Typography>
                    </Card>
                  </Link>
                </Tooltip>
              </Grid>
            ))}
          </Grid>

        </Box>
      </FlexBox>
    </Layout>
  );
};

export default Dashboard;
